/**
 * AVBP Intake SERVICE CATALOG ADAPTER — the seam between the intake stage
 * engine and the (parallel-session-owned) service catalogue.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER'S DIRECTION (honoured here):
 *    "DO NOT build our own serviceCatalog — a PARALLEL session owns it. You
 *     consume it through a clean adapter interface. Service name + module-
 *     selection are read LIVE from the catalog, never hardcoded → when a
 *     service is renamed or its module-selection changes, every intake form
 *     adapts automatically."
 *
 *  This module defines the ADAPTER INTERFACE (`IntakeServiceCatalog`) the stage
 *  engine consumes, plus a LOCAL implementation (`spineIntakeCatalog`) sourced
 *  from the spine `tier_templates` so EVERY service renders TODAY. The parallel
 *  session's `serviceCatalog` drops in with a ONE-LINE swap (see CONTRACT
 *  below) — set `activeIntakeCatalog` to an adapter backed by their catalog.
 *
 *  ─── THE CONTRACT FOR THE PARALLEL SESSION (additive, keep it additive) ───
 *  To make their `serviceCatalog` the source of intake stages + names, add ONE
 *  optional field to each `ServiceCatalogEntry` (rateCard.ts):
 *
 *    interface ServiceCatalogEntry {
 *      // ...existing fields (svcId, name, desc, tier, unit, rINR, ...)...
 *      intakeStages?: IntakeStageSpec[];   // ← ADD THIS (optional, additive)
 *    }
 *
 *    interface IntakeStageSpec {
 *      stageKey:   string;                 // stable id persisted in stages.{id}
 *      title:      string;                 // client-facing stage header
 *      blurb?:     string;                 // one-line helper
 *      moduleKeys: string[];              // spine module_registry keys, in order
 *      fieldFlags?: Record<string, 'mandatory' | 'on' | 'off'>;
 *                                          // per-module flag (key → flag)
 *    }
 *
 *  Then build an adapter over their catalogue and swap it in:
 *
 *    export const activeIntakeCatalog: IntakeServiceCatalog =
 *      catalogFromServiceEntries(REFRESHED_CATALOGUE);   // ← the one-line swap
 *
 *  where `catalogFromServiceEntries` (provided below) maps each entry's
 *  `name` → name(tier) and `intakeStages` → intakeStages(tier). Until they add
 *  the field, that helper FALLS BACK to the spine-derived stages, so nothing
 *  breaks during the hand-off. NOTE: the parallel `ServiceCatalogEntry.name`
 *  ALREADY carries the canonical Set-4 names — so even without `intakeStages`,
 *  pointing the catalog's NAME source at their entries is a safe partial swap.
 *
 *  This file is Firebase-free + React-free (pure) so the `.cjs` test runner
 *  exercises name + stage resolution for every service.
 * ─────────────────────────────────────────────────────────────────────────
 */

import {
  GROUP_ORDER,
  GROUP_TITLE,
  moduleByKey,
  type ModuleFlag,
  type ModuleGroup,
} from './intakeModules';

/* ─────────────────────────── the contract types ───────────────────────── */

/**
 * One intake stage spec — an ORDERED group of module keys + per-module flags.
 * This is EXACTLY the shape the parallel session adds to `ServiceCatalogEntry`
 * as the optional `intakeStages?: IntakeStageSpec[]` field.
 */
export interface IntakeStageSpec {
  /** Stable id persisted in the doc's `stages.{stageKey}` map. */
  stageKey: string;
  /** Client-facing stage header. */
  title: string;
  /** One-line helper under the title. */
  blurb?: string;
  /** spine module_registry keys, in render order. */
  moduleKeys: string[];
  /** Per-module flag (moduleKey → 'mandatory'|'on'|'off'). */
  fieldFlags?: Record<string, ModuleFlag>;
}

/**
 * The adapter interface the stage engine + the Intake route consume. The intake
 * NEVER reads a service name or a module-selection from anywhere else — only
 * through this. Swap the implementation to change the source with zero rework.
 */
export interface IntakeServiceCatalog {
  /** The client-facing service name for a tier (live, never hardcoded). */
  name(tier: string): string;
  /** The ordered intake stages (module selection) for a tier. */
  intakeStages(tier: string): IntakeStageSpec[];
  /** All service tier keys this catalog knows (for tooling/tests). */
  tiers(): string[];
}

/* ──────────────────── the LOCAL source: spine tier_templates ───────────── */
/*
 * Transcribed from
 *   AVBP_MODULAR_INTAKE_REPORT_SPINE_CONFIG_v1_2026-06-14.json → tier_templates
 * (the `name` + `modules` map per service). This is the LOCAL source so every
 * service renders now; the parallel `serviceCatalog` replaces it via the swap
 * above. The `name`s here mirror the canonical Set-4 client-facing names
 * (rateCard REFRESHED_CATALOGUE) so the local fallback matches what the
 * parallel catalog will return — a rename there just overrides here.
 */

interface SpineTierTemplate {
  name: string;
  modules: Record<string, ModuleFlag>;
}

/**
 * Service names: canonical Set-4 (client-facing), matching rateCard
 * REFRESHED_CATALOGUE. The spine JSON's older names ("Selection Path · Disha",
 * "Concept Design") are deliberately UPGRADED to the Set-4 canon here so the
 * local fallback never shows a stale name. (The parallel catalog is the live
 * authority once swapped in.)
 */
export const SPINE_TIER_TEMPLATES: Record<string, SpineTierTemplate> = {
  // SCOPING (2026-06-22, AD-031) — the UNIVERSAL, service-agnostic pre-quote form
  // sent to EVERY new lead. The tier is unknown at scoping time, so it captures
  // only enough to understand project TYPE + QUANTUM so staff can quote: contact
  // (email + WhatsApp via l1a_about_you), intended use, an optional "what's on
  // your mind", and property basics (site location + rough size/floors + optional
  // photos/plan). It REUSES the L1A redesign modules — which write the SAME shared
  // `answers` doc — so when the deal converts to a delivery tier, those answers
  // carry over automatically (the whole point of "modular"). Explicit stage order
  // lives in SCOPING_STAGES below (Property before People-less; no consent-heavy
  // technical modules). Consent is the engine's always-on Stage 0.
  SCOPING: {
    name: 'Project Scoping',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      l1a_concerns: 'on',
      l1a_property_details: 'mandatory',
    },
  },
  L1A: {
    // L1A redesign (2026-06-21) — "The Selection Path · Disha". The redesigned
    // L1A modules ONLY. Grid / entrances / AUO / north / candidate_properties /
    // property_media are EVALUATION-STAGE (staff technical panel), removed from
    // the client intake. The explicit stage ORDER is in L1A_STAGES below (the
    // group-grouping mechanic puts person before property_technical, but L1A wants
    // Property BEFORE People — so L1A uses an explicit stage list).
    name: 'The Selection Path · Disha',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      l1a_concerns: 'on',
      l1a_property_details: 'mandatory',
      l1a_people: 'mandatory',
    },
  },
  L1B: {
    // L1B redesign (2026-06-23) — "The Clarity Path · Darshan". Built on the live
    // L1A engine. About-you = the L1A about-you module VERBATIM (owner ruling
    // 2026-06-23: the L1A About-you is complete/sufficient for every level — no
    // property-address field inside About you; the site location/address lives in
    // Property details). Then goal/use · concerns · single-property drawing-gate
    // property details · People & astrology (l1a_people, incl. per-person health)
    // · space & occupancy · plans & willingness. Grid/entrances/AUO/north/boundary
    // stay in the staff Technical Panel. Explicit stage order in L1B_STAGES.
    name: 'The Clarity Path · Darshan',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      l1a_concerns: 'mandatory',
      l1b_property_details: 'mandatory',
      l1a_people: 'mandatory',
      occupancy_history: 'on',
      l1b_plans_preferences: 'mandatory',
    },
  },
  // L1C (2026-06-21 spec, moved to correct ladder position after L1B 2026-06-23) —
  // "The Review Path · Sameeksha". A ₹11,000 report-only PLAN REVIEW of an
  // architect's drawing set. About-you = the L1A about-you module VERBATIM (owner
  // ruling 2026-06-23). Site location/address is captured on the drawings_upload
  // stage (architect fields HIDDEN by the bright line). Order (owner 2026-06-23):
  // About you · Goal · What's on your mind? (concerns BEFORE drawings) · Drawings ·
  // People & astrology. Explicit stage order in L1C_STAGES. The enum/data key is
  // `L1C` everywhere (Firestore, rules, quote.tier, report map, analytics).
  L1C: {
    name: 'The Review Path · Sameeksha',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      drawings_upload: 'mandatory',
      l1a_goal_use: 'mandatory',
      presenting_concerns: 'on',
      l1a_people: 'mandatory',
    },
  },
  L2A: {
    // L2A redesign (owner spec 2026-06-25) — "The Layout Path · Mandala".
    // Assembles the SHARED modules (config-driven reuse), NOT bespoke ones:
    // About you (l1a_about_you) · Goal (l1a_goal_use) · What's on your mind
    // (presenting_concerns, optional) · Drawings & plans (drawings_upload,
    // L1C-style single set, architect contact hidden) · People & astrology
    // (l1a_people, full) · Design brief (design_programme, with L2A SECTION
    // FLAGS — see TIER_SECTION_FLAGS.L2A in designProgramme.ts). KEEP the existing
    // occupancy_status + map_availability modules. Explicit head in L2A_STAGES.
    name: 'The Layout Path · Mandala',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      presenting_concerns: 'on',
      drawings_upload: 'mandatory',
      l1a_people: 'mandatory',
      design_programme: 'mandatory',
      occupancy_status: 'on',
      map_availability: 'on',
    },
  },
  L2B: {
    // L2B redesign (AD-042, 2026-06-25) — "The Alignment Path · Nidana". MIRRORS
    // L1B's shared-module assembly (About you · Goal · Concerns · Property details
    // · People & astrology · Occupancy · Plans & preferences) PLUS L2B's existing
    // "Background & readiness" (= the `diagnostic` group). Heavy technical
    // (floor_geometry_grid · entrances · auo_fixtures · property_media ·
    // property_drawings) moves to the staff Technical Panel (as L1B). NO design
    // brief. `health_history` DROPPED (now covered in People & astrology). Explicit
    // head in L2B_STAGES; the diagnostic group appends via stagesFromSelection.
    name: 'The Alignment Path · Nidana',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      l1a_concerns: 'mandatory',
      l1b_property_details: 'mandatory',
      l1a_people: 'mandatory',
      occupancy_history: 'on',
      l1b_plans_preferences: 'mandatory',
      // Background & readiness (the `diagnostic` group — keep current flags):
      prior_remedies_tried: 'on',
      structural_willingness: 'on',
      implementation_timeline: 'on',
      decision_maker_map: 'on',
      attached_units: 'on',
      use_constraints: 'on',
      renovation_appetite: 'on',
    },
  },
  L2C: {
    // L2C label PATCHED → "Integration Path · Samanvaya" (2026-06-21). The enum/
    // data KEY stays `L2C` everywhere — only the human label flips. L2C shows the
    // architect-contact fields on drawings_upload (architectCoordination:true).
    // About-you (owner ruling 2026-06-23): the L1A about-you module VERBATIM — no
    // property-address field inside About you; the site location/address lives in
    // the drawings/property stage. People & astrology = l1a_people (full astrology
    // incl. per-person health). Order (owner 2026-06-23): About you · Goal · What's
    // on your mind? (concerns BEFORE drawings/property) · Drawings · People &
    // astrology · its technical/design stages. Explicit head in L2C_STAGES; the
    // remaining technical/design modules fall through stagesFromSelection.
    name: 'The Integration Path · Samanvaya',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      presenting_concerns: 'on',
      // UNIFIED Property & drawings block (2026-06-24 FULL MERGE) — the four
      // former stub modules (property_media · space_survey · property_drawings ·
      // property_constraints) are now folded IN as the "About the building"
      // section of drawings_upload, and the separate `floor_geometry_grid` +
      // `entrances` CLIENT intake stages are removed (the grid is traced inside
      // the property's Concept▸Version▸Floor record; entrances are staff/
      // evaluation, captured on the staff technical panel, never client intake).
      // The L2C-only architect + interior-designer coordination (structured
      // Name·Email·WhatsApp) also lives inside drawings_upload (bright line).
      drawings_upload: 'mandatory',
      l1a_people: 'mandatory',
      design_programme: 'mandatory',
      occupancy_status: 'on',
      site_visit_readiness: 'on',
      map_availability: 'on',
      prior_engagement_link: 'on',
    },
  },
  L2D: {
    // L2D redesign (AD-042, 2026-06-25) — RENAMED "The Build Path · Nirmana" →
    // "The Detailing Path · Vistara" (data key L2D unchanged; L4A · Nirmana is the
    // reserved future build tier). A lean TECHNICAL form: About you · Goal ·
    // Drawings & plans (the approved design we detail FROM) · Design brief (L2D
    // section flags) · Construction details (EXPANDED 11 groups). DROPS What's-on-
    // your-mind + People-&-astrology. Explicit head in L2D_STAGES.
    name: 'The Detailing Path · Vistara',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      drawings_upload: 'mandatory',
      design_programme: 'mandatory',
      construction_details: 'mandatory',
    },
  },
  L3A: {
    // L3A redesign (AD-042, 2026-06-25) — "The Brief Path · Sankalpa". Shared
    // modules: About you · Goal · What's on your mind (optional) · Drawings & plans
    // (OPTIONAL — brief-path is often greenfield) · People & astrology · Design
    // brief (FULL depth, all A–H ON). Explicit head in L3A_STAGES.
    name: 'The Brief Path · Sankalpa',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      presenting_concerns: 'on',
      drawings_upload: 'on',
      l1a_people: 'mandatory',
      design_programme: 'mandatory',
    },
  },
  L3B: {
    // L3B redesign (AD-042, 2026-06-25) — "The Design Path · Sthapana". Like L2C
    // (full design brief, all A–H ON) PLUS a NEW "Plans & preferences" stage.
    // Shared modules: About you · Goal · Concerns · Drawings & plans · People &
    // astrology · Design brief · Plans & preferences. Explicit head in L3B_STAGES.
    name: 'The Design Path · Sthapana',
    modules: {
      l1a_about_you: 'mandatory',
      consent_permissions: 'on',
      l1a_goal_use: 'mandatory',
      presenting_concerns: 'on',
      drawings_upload: 'mandatory',
      l1a_people: 'mandatory',
      design_programme: 'mandatory',
      l3b_plans_preferences: 'mandatory',
    },
  },
  ASTROLOGY: {
    name: 'Astrology Consultation',
    modules: {
      identity_property: 'mandatory',
      consent_permissions: 'on',
      person_birth_data: 'mandatory',
      family_map: 'on',
      astro_thread_remedy: 'on',
    },
  },
  WELLNESS: {
    name: 'Wellness / Ayurveda',
    modules: {
      identity_property: 'mandatory',
      consent_permissions: 'on',
      person_birth_data: 'on',
      health_history: 'mandatory',
      presenting_concerns: 'on',
    },
  },
  ANCILLARY_SITE_VISIT: {
    name: 'Site Visit',
    modules: { site_visit_request: 'mandatory', consent_permissions: 'on' },
  },
  ANCILLARY_SURVEY_MAP: {
    name: 'Survey & Map Making',
    modules: { survey_map_request: 'mandatory', consent_permissions: 'on' },
  },
  ANCILLARY_MAP_GRADING: {
    name: 'Map Grading',
    modules: { map_grading_request: 'mandatory', consent_permissions: 'on' },
  },
};

/**
 * Map a resolver tier (e.g. `L2B_R1`, `L2B`) to its spine template key. The
 * stage engine resumes from `L2B_R1`-style tiers; the catalog is keyed by the
 * SERVICE key (`L2B`). Strip a trailing `_R{n}` round suffix so both resolve.
 */
export function spineTierKey(tier: string): string {
  return (tier || '').replace(/_R\d+$/i, '').toUpperCase();
}

/**
 * PURE — the service CODE for a tier (`L1A`, `L1B`, `L2B`, `L3B`, …). Empty for
 * non-laddered services (Astrology, Wellness, the ancillaries), which have a
 * name but no L-code. Owner ruling 2026-06-22: staff identify a service by its
 * CODE first, so the code leads every label.
 */
export function serviceCode(tier: string): string {
  const key = spineTierKey(tier);
  return /^L[0-9]/.test(key) ? key : '';
}

/**
 * PURE — the ONE canonical label shown EVERYWHERE a service is named:
 * `"L1A · The Selection Path · Disha"` (laddered) or just the name
 * (`"Astrology Consultation"`) when there is no L-code. Fix this at the fetch
 * layer (owner ruling 2026-06-22) so the dropdown, the form header, quotes and
 * every other surface read the same string — code + name, never one or the
 * other. Sources the name LIVE from the catalog (default: the active catalog).
 */
export function serviceLabel(
  tier: string,
  catalog: IntakeServiceCatalog = activeIntakeCatalog,
): string {
  const code = serviceCode(tier);
  const name = catalog.name(tier);
  return code ? `${code} · ${name}` : name;
}

/* ──────────── derive ordered stages from a tier_template selection ──────── */

/**
 * PURE — turn a `{moduleKey: flag}` selection into ordered IntakeStageSpecs.
 * Modules flagged 'off' are dropped. `consent_permissions` is REMOVED here —
 * the stage engine prepends the canonical Consent Stage 0 itself (so consent is
 * never duplicated and never reorderable). The rest are grouped by their spine
 * `group` in `GROUP_ORDER`, one stage per non-empty group, in canonical order.
 */
export function stagesFromSelection(
  selection: Record<string, ModuleFlag>,
): IntakeStageSpec[] {
  const byGroup = new Map<ModuleGroup, string[]>();
  for (const [key, flag] of Object.entries(selection)) {
    if (flag === 'off') continue;
    if (key === 'consent_permissions') continue; // engine owns Stage 0
    const def = moduleByKey(key);
    if (!def) continue; // unknown module key → skip (logged via tests)
    const arr = byGroup.get(def.group) || [];
    arr.push(key);
    byGroup.set(def.group, arr);
  }
  const stages: IntakeStageSpec[] = [];
  for (const group of GROUP_ORDER) {
    const keys = byGroup.get(group);
    if (!keys || !keys.length) continue;
    const fieldFlags: Record<string, ModuleFlag> = {};
    for (const k of keys) fieldFlags[k] = selection[k];
    stages.push({
      stageKey: group,
      title: GROUP_TITLE[group],
      moduleKeys: keys,
      fieldFlags,
    });
  }
  return stages;
}

/* ───────────────────── L1A redesign — explicit ordered stages ──────────── */

/**
 * L1A (2026-06-21) — EXPLICIT ordered stage list. The generic group-grouping
 * (`stagesFromSelection`) orders by spine group, which puts `person` before
 * `property_technical`; L1A's owner-approved order is Property BEFORE People, so
 * L1A defines its stages explicitly. Consent is NOT here — the stage engine
 * prepends the always-on Consent Stage 0 itself. Stage keys are stable ids
 * persisted in `stages.{id}`.
 */
export const L1A_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and where the report is going.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & intended use',
    blurb: 'What are you selecting a property for?',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Issue and/or aspiration per area.',
    moduleKeys: ['l1a_concerns'],
    fieldFlags: { l1a_concerns: 'on' },
  },
  {
    stageKey: 'property',
    title: 'Property details',
    blurb: 'The candidate properties you are comparing.',
    moduleKeys: ['l1a_property_details'],
    fieldFlags: { l1a_property_details: 'mandatory' },
  },
  {
    stageKey: 'people',
    title: 'People',
    blurb: 'Who uses the space — with per-person astrology.',
    moduleKeys: ['l1a_people'],
    fieldFlags: { l1a_people: 'mandatory' },
  },
];

/* ───────────────────── L1B redesign — explicit ordered stages ──────────── */

/**
 * L1B (2026-06-23) — "The Clarity Path · Darshan". EXPLICIT ordered stages, like
 * L1A (Property BEFORE People). Built on the live L1A engine. About you = the L1A
 * about-you module VERBATIM (owner ruling 2026-06-23 — no property-address field
 * inside About you; the site location/address is captured in Property details).
 * Consent is NOT here — the stage engine appends the always-on Consent / "Review &
 * submit" stage. Maps to the approved L1B mockup boxes.
 */
export const L1B_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and where the report is going.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & use of this space',
    blurb: 'What this space is for, and your goal — in your own words.',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Issue and/or aspiration per area.',
    moduleKeys: ['l1a_concerns'],
    fieldFlags: { l1a_concerns: 'mandatory' },
  },
  {
    stageKey: 'property',
    title: 'Property details',
    blurb: 'The space you occupy — site location, area, drawing & media.',
    moduleKeys: ['l1b_property_details'],
    fieldFlags: { l1b_property_details: 'mandatory' },
  },
  {
    stageKey: 'people',
    title: 'People & astrology',
    blurb: 'Who lives/works here — with per-person astrology & health.',
    moduleKeys: ['l1a_people'],
    fieldFlags: { l1a_people: 'mandatory' },
  },
  {
    stageKey: 'occupancy',
    title: 'About the space & occupancy',
    blurb: 'How it’s used, since when, anything notable since you moved in.',
    moduleKeys: ['occupancy_history'],
    fieldFlags: { occupancy_history: 'on' },
  },
  {
    stageKey: 'plans',
    title: 'Your plans & preferences',
    blurb: 'What you’ve tried + your willingness to act.',
    moduleKeys: ['l1b_plans_preferences'],
    fieldFlags: { l1b_plans_preferences: 'mandatory' },
  },
];

/* ───────────────────── SCOPING — explicit ordered stages ──────────────── */

/**
 * SCOPING (2026-06-22, AD-031) — the universal pre-quote stage list. Mirrors the
 * L1A redesign order (About you → Goal → What's on your mind → Property details)
 * but STOPS before the People/birth-data stage — scoping only needs project TYPE
 * + QUANTUM to quote, not a full astrology capture. The later delivery-tier
 * intake (with its own People stage) resumes the SAME shared answers doc, so the
 * contact + goal + property answers captured here carry straight over.
 */
export const SCOPING_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and how we can reach you.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & intended use',
    blurb: 'What is the project for?',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Anything you want us to know (optional).',
    moduleKeys: ['l1a_concerns'],
    fieldFlags: { l1a_concerns: 'on' },
  },
  {
    stageKey: 'property',
    title: 'Property basics',
    blurb: 'Where it is and roughly how big — so we can scope and quote.',
    moduleKeys: ['l1a_property_details'],
    fieldFlags: { l1a_property_details: 'mandatory' },
  },
];

/* ───────────── L1C redesign — explicit ordered stages (2026-06-23) ──────── */

/**
 * L1C — "The Review Path · Sameeksha". EXPLICIT ordered stages (owner ruling
 * 2026-06-23). About you = the L1A about-you module VERBATIM (no property-address
 * field — the site location/address is captured on the Drawings & plan stage).
 * Goal/use gets a tier-aware renderer ("What uses are you planning in this
 * property?"). Order: About you · Goal · What's on your mind? (concerns BEFORE
 * drawings) · Drawings & plan · People & astrology. The People stage uses the FULL
 * l1a_people module (per-person astrology + Health, default-ON birth details) —
 * the same module L1B uses, not a bare people list. Consent = engine Stage 0.
 */
export const L1C_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and where the report is going.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & intended use',
    blurb: 'What uses are you planning in this property?',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Any specific Vastu concerns about this plan (optional).',
    moduleKeys: ['presenting_concerns'],
    fieldFlags: { presenting_concerns: 'on' },
  },
  {
    stageKey: 'drawings',
    title: 'Drawings & plan',
    blurb: "The architect's drawings we will review — with the site location.",
    moduleKeys: ['drawings_upload'],
    fieldFlags: { drawings_upload: 'mandatory' },
  },
  {
    stageKey: 'people',
    title: 'People & astrology',
    blurb: 'Who will occupy the property — with per-person astrology & health.',
    moduleKeys: ['l1a_people'],
    fieldFlags: { l1a_people: 'mandatory' },
  },
];

/* ───────────── L2C redesign — explicit ordered head stages (2026-06-23) ──── */

/**
 * L2C — "The Integration Path · Samanvaya". EXPLICIT ordered HEAD stages (owner
 * ruling 2026-06-23): About you · Goal · What's on your mind? (concerns BEFORE
 * the drawings/property stage) · Drawings & plan · People & astrology. About you =
 * the L1A about-you module VERBATIM (no property-address field — site location/
 * address lives on the drawings stage). People & astrology = the FULL l1a_people
 * module (same as L1B). The REMAINING L2C technical/design modules (floor grid,
 * entrances, space survey, design programme, etc.) are appended after this head
 * via stagesFromSelection over the modules NOT already placed here. Consent =
 * engine Stage 0.
 */
export const L2C_HEAD_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and where the report is going.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & intended use',
    blurb: 'What uses are you planning in this property?',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Any specific Vastu concerns about this plan (optional).',
    moduleKeys: ['presenting_concerns'],
    fieldFlags: { presenting_concerns: 'on' },
  },
  {
    stageKey: 'drawings',
    title: 'Drawings & plan',
    blurb: "The architect's drawings and all revised versions — with the site location.",
    moduleKeys: ['drawings_upload'],
    fieldFlags: { drawings_upload: 'mandatory' },
  },
  {
    stageKey: 'people',
    title: 'People & astrology',
    blurb: 'Who will occupy the property — with per-person astrology & health.',
    moduleKeys: ['l1a_people'],
    fieldFlags: { l1a_people: 'mandatory' },
  },
];

/**
 * PURE — the modules placed by L2C_HEAD_STAGES (so stagesFromSelection over the
 * REST doesn't double-render them). Includes consent (engine owns it separately).
 */
const L2C_HEAD_MODULE_KEYS = new Set<string>([
  'consent_permissions',
  ...L2C_HEAD_STAGES.flatMap((s) => s.moduleKeys),
]);

/* ───────────── L2A redesign — explicit ordered stages (owner spec 2026-06-25) ──── */

/**
 * L2A — "The Layout Path · Mandala". EXPLICIT ordered stages (owner spec
 * 2026-06-25). Assembles SHARED modules (config-driven reuse): About you
 * (l1a_about_you) · Goal & use (l1a_goal_use) · What's on your mind
 * (presenting_concerns, OPTIONAL) · Drawings & plans (drawings_upload, L1C-style
 * single set — `singleDrawingSet` flag; architect contact hidden, coordination
 * in the design brief) · People & astrology (l1a_people, full) · Design brief
 * (design_programme, with L2A SECTION FLAGS). The remaining KEEP modules
 * (occupancy_status, map_availability) append after the head. Consent = engine
 * Stage 0. Design-brief section depth = `TIER_SECTION_FLAGS.L2A`.
 */
export const L2A_STAGES: IntakeStageSpec[] = [
  {
    stageKey: 'about_you',
    title: 'About you',
    blurb: 'Your details and where the report is going.',
    moduleKeys: ['l1a_about_you'],
    fieldFlags: { l1a_about_you: 'mandatory' },
  },
  {
    stageKey: 'goal_use',
    title: 'Your goal & use of the space',
    blurb: 'What uses are you planning in this property?',
    moduleKeys: ['l1a_goal_use'],
    fieldFlags: { l1a_goal_use: 'mandatory' },
  },
  {
    stageKey: 'concerns',
    title: "What's on your mind?",
    blurb: 'Any specific Vastu concerns about this plan (optional).',
    moduleKeys: ['presenting_concerns'],
    fieldFlags: { presenting_concerns: 'on' },
  },
  {
    stageKey: 'drawings',
    title: 'Drawings & plans',
    blurb: "The architect's drawing set — with the site location.",
    moduleKeys: ['drawings_upload'],
    fieldFlags: { drawings_upload: 'mandatory' },
  },
  {
    stageKey: 'people',
    title: 'People & astrology',
    blurb: 'Who will occupy the property — with per-person astrology & health.',
    moduleKeys: ['l1a_people'],
    fieldFlags: { l1a_people: 'mandatory' },
  },
  {
    stageKey: 'design_brief',
    title: 'Design brief',
    blurb: 'The requirements brief — spaces, lifestyle, adjacencies, constraints.',
    moduleKeys: ['design_programme'],
    fieldFlags: { design_programme: 'mandatory' },
  },
  {
    stageKey: 'about_space',
    title: 'About the space',
    blurb: 'Occupancy status and whether a measured / CAD map is available.',
    moduleKeys: ['occupancy_status', 'map_availability'],
    fieldFlags: { occupancy_status: 'on', map_availability: 'on' },
  },
];

/** PURE — modules placed by L2A_STAGES (so stagesFromSelection over the rest
 * doesn't double-render them). Includes consent (engine owns it separately). */
const L2A_HEAD_MODULE_KEYS = new Set<string>([
  'consent_permissions',
  ...L2A_STAGES.flatMap((s) => s.moduleKeys),
]);

/* ───────────── L2B (AD-042) — mirror L1B + Background & readiness ─────────── */

/**
 * L2B — "The Alignment Path · Nidana". EXPLICIT ordered stages mirroring L1B
 * (shared modules), then L2B's existing "Background & readiness" (the diagnostic
 * group) appended via stagesFromSelection. NO design brief; heavy technical on
 * the staff Technical Panel; health_history dropped (covered in People).
 */
export const L2B_STAGES: IntakeStageSpec[] = [
  { stageKey: 'about_you', title: 'About you', blurb: 'Your details and where the report is going.', moduleKeys: ['l1a_about_you'], fieldFlags: { l1a_about_you: 'mandatory' } },
  { stageKey: 'goal_use', title: 'Your goal & use of the space', blurb: 'What this space is for, and your goal.', moduleKeys: ['l1a_goal_use'], fieldFlags: { l1a_goal_use: 'mandatory' } },
  { stageKey: 'concerns', title: "What's on your mind?", blurb: 'Issue and/or aspiration per area.', moduleKeys: ['l1a_concerns'], fieldFlags: { l1a_concerns: 'mandatory' } },
  { stageKey: 'property', title: 'Property details', blurb: 'The space you occupy — site location, area, drawing & media.', moduleKeys: ['l1b_property_details'], fieldFlags: { l1b_property_details: 'mandatory' } },
  { stageKey: 'people', title: 'People & astrology', blurb: 'Who lives/works here — with per-person astrology & health.', moduleKeys: ['l1a_people'], fieldFlags: { l1a_people: 'mandatory' } },
  { stageKey: 'occupancy', title: 'About the space & occupancy', blurb: 'How it’s used, since when, anything notable since you moved in.', moduleKeys: ['occupancy_history'], fieldFlags: { occupancy_history: 'on' } },
  { stageKey: 'plans', title: 'Your plans & preferences', blurb: 'What you’ve tried + your willingness to act.', moduleKeys: ['l1b_plans_preferences'], fieldFlags: { l1b_plans_preferences: 'mandatory' } },
];
const L2B_HEAD_MODULE_KEYS = new Set<string>(['consent_permissions', ...L2B_STAGES.flatMap((s) => s.moduleKeys)]);

/* ───────────── L2D · Vistara (AD-042) — lean technical detailing ──────────── */

/**
 * L2D — "The Detailing Path · Vistara". EXPLICIT ordered stages: About you · Goal
 * · Drawings & plans · Design brief · Construction details (11 groups). No
 * concerns / no people-astrology. The drawings are multi-version (not L1C-single)
 * because L2D detailing iterates on the approved design.
 */
export const L2D_STAGES: IntakeStageSpec[] = [
  { stageKey: 'about_you', title: 'About you', blurb: 'Your details and where the report is going.', moduleKeys: ['l1a_about_you'], fieldFlags: { l1a_about_you: 'mandatory' } },
  { stageKey: 'goal_use', title: 'Your goal & intended use', blurb: 'What uses are you planning in this property?', moduleKeys: ['l1a_goal_use'], fieldFlags: { l1a_goal_use: 'mandatory' } },
  { stageKey: 'drawings', title: 'Drawings & plans', blurb: 'The approved design we detail from — with the site location.', moduleKeys: ['drawings_upload'], fieldFlags: { drawings_upload: 'mandatory' } },
  { stageKey: 'design_brief', title: 'Design brief', blurb: 'Special functions, style, budget, constraints & the project team.', moduleKeys: ['design_programme'], fieldFlags: { design_programme: 'mandatory' } },
  { stageKey: 'construction', title: 'Construction details', blurb: 'Stage & structure, services, materials, soil, approvals — for detailing.', moduleKeys: ['construction_details'], fieldFlags: { construction_details: 'mandatory' } },
];
const L2D_HEAD_MODULE_KEYS = new Set<string>(['consent_permissions', ...L2D_STAGES.flatMap((s) => s.moduleKeys)]);

/* ───────────── L3A · Sankalpa (AD-042) — brief path (drawings optional) ───── */

/**
 * L3A — "The Brief Path · Sankalpa". About you · Goal · What's on your mind
 * (optional) · Drawings & plans (OPTIONAL — often greenfield) · People &
 * astrology · Design brief (FULL depth). Explicit head; no trailing modules.
 */
export const L3A_STAGES: IntakeStageSpec[] = [
  { stageKey: 'about_you', title: 'About you', blurb: 'Your details and where the report is going.', moduleKeys: ['l1a_about_you'], fieldFlags: { l1a_about_you: 'mandatory' } },
  { stageKey: 'goal_use', title: 'Your goal & intended use', blurb: 'What uses are you planning in this property?', moduleKeys: ['l1a_goal_use'], fieldFlags: { l1a_goal_use: 'mandatory' } },
  { stageKey: 'concerns', title: "What's on your mind?", blurb: 'Any specific Vastu concerns (optional).', moduleKeys: ['presenting_concerns'], fieldFlags: { presenting_concerns: 'on' } },
  { stageKey: 'drawings', title: 'Drawings & plans', blurb: 'The plot or any existing design (optional — brief-path is often greenfield).', moduleKeys: ['drawings_upload'], fieldFlags: { drawings_upload: 'on' } },
  { stageKey: 'people', title: 'People & astrology', blurb: 'Who will occupy the property — with per-person astrology & health.', moduleKeys: ['l1a_people'], fieldFlags: { l1a_people: 'mandatory' } },
  { stageKey: 'design_brief', title: 'Design brief', blurb: 'The full requirements brief — spaces, lifestyle, adjacencies, style, budget, constraints & team.', moduleKeys: ['design_programme'], fieldFlags: { design_programme: 'mandatory' } },
];
const L3A_HEAD_MODULE_KEYS = new Set<string>(['consent_permissions', ...L3A_STAGES.flatMap((s) => s.moduleKeys)]);

/* ───────────── L3B · Sthapana (AD-042) — design path + Plans & preferences ── */

/**
 * L3B — "The Design Path · Sthapana". Like L2C (full design brief, all A–H ON)
 * PLUS a NEW "Plans & preferences" stage. Explicit head; no trailing modules.
 */
export const L3B_STAGES: IntakeStageSpec[] = [
  { stageKey: 'about_you', title: 'About you', blurb: 'Your details and where the report is going.', moduleKeys: ['l1a_about_you'], fieldFlags: { l1a_about_you: 'mandatory' } },
  { stageKey: 'goal_use', title: 'Your goal & intent of use', blurb: 'What uses are you planning in this property?', moduleKeys: ['l1a_goal_use'], fieldFlags: { l1a_goal_use: 'mandatory' } },
  { stageKey: 'concerns', title: "What's on your mind?", blurb: 'Any specific Vastu concerns (optional).', moduleKeys: ['presenting_concerns'], fieldFlags: { presenting_concerns: 'on' } },
  { stageKey: 'drawings', title: 'Drawings & plans', blurb: "The architect's drawings and revised versions — with the site location.", moduleKeys: ['drawings_upload'], fieldFlags: { drawings_upload: 'mandatory' } },
  { stageKey: 'people', title: 'People & astrology', blurb: 'Who will occupy the property — with per-person astrology & health.', moduleKeys: ['l1a_people'], fieldFlags: { l1a_people: 'mandatory' } },
  { stageKey: 'design_brief', title: 'Design brief', blurb: 'The full requirements brief — spaces, lifestyle, adjacencies, style, budget, constraints & team.', moduleKeys: ['design_programme'], fieldFlags: { design_programme: 'mandatory' } },
  { stageKey: 'plans_prefs', title: 'Plans & preferences', blurb: 'Your plan-in-mind, decision-maker, approvals, references, non-negotiables & Vastu stance.', moduleKeys: ['l3b_plans_preferences'], fieldFlags: { l3b_plans_preferences: 'mandatory' } },
];
const L3B_HEAD_MODULE_KEYS = new Set<string>(['consent_permissions', ...L3B_STAGES.flatMap((s) => s.moduleKeys)]);

/* ───────────────────── the LOCAL catalog implementation ────────────────── */

/**
 * The local (spine-sourced) catalog. Renders every service NOW. The parallel
 * session's catalog replaces this via `activeIntakeCatalog` (see top-of-file
 * CONTRACT). Unknown tier → empty stages + the tier string as a last-resort
 * name (the engine still prepends Consent so the flow is never empty).
 */
export const spineIntakeCatalog: IntakeServiceCatalog = {
  name(tier: string): string {
    const t = SPINE_TIER_TEMPLATES[spineTierKey(tier)];
    return t ? t.name : tier;
  },
  intakeStages(tier: string): IntakeStageSpec[] {
    const key = spineTierKey(tier);
    // SCOPING (AD-031) — explicit ordered stage list (universal pre-quote form).
    if (key === 'SCOPING') return SCOPING_STAGES.map((s) => ({ ...s }));
    // L1A redesign — explicit ordered stage list (Property before People).
    if (key === 'L1A') return L1A_STAGES.map((s) => ({ ...s }));
    // L1B redesign — explicit ordered stage list (Property before People).
    if (key === 'L1B') return L1B_STAGES.map((s) => ({ ...s }));
    // L1C redesign (2026-06-23) — explicit ordered stage list (About you → Goal →
    // What's on your mind? → Drawings & plan → People & astrology).
    if (key === 'L1C') return L1C_STAGES.map((s) => ({ ...s }));
    const t = SPINE_TIER_TEMPLATES[key];
    if (!t) return [];
    // Explicit-HEAD tiers (AD-042): an ordered head of shared-module stages, then
    // the REMAINING selected modules appended via stagesFromSelection over the
    // modules NOT already in the head. One config map drives all of them.
    const HEAD_TIERS: Record<string, { head: IntakeStageSpec[]; headKeys: Set<string> }> = {
      L2C: { head: L2C_HEAD_STAGES, headKeys: L2C_HEAD_MODULE_KEYS },
      L2A: { head: L2A_STAGES, headKeys: L2A_HEAD_MODULE_KEYS },
      L2B: { head: L2B_STAGES, headKeys: L2B_HEAD_MODULE_KEYS },
      L2D: { head: L2D_STAGES, headKeys: L2D_HEAD_MODULE_KEYS },
      L3A: { head: L3A_STAGES, headKeys: L3A_HEAD_MODULE_KEYS },
      L3B: { head: L3B_STAGES, headKeys: L3B_HEAD_MODULE_KEYS },
    };
    const ht = HEAD_TIERS[key];
    if (ht) {
      const head = ht.head.map((s) => ({ ...s }));
      const restSelection: Record<string, ModuleFlag> = {};
      for (const [mk, flag] of Object.entries(t.modules)) {
        if (ht.headKeys.has(mk)) continue;
        restSelection[mk] = flag;
      }
      return [...head, ...stagesFromSelection(restSelection)];
    }
    return stagesFromSelection(t.modules);
  },
  tiers(): string[] {
    return Object.keys(SPINE_TIER_TEMPLATES);
  },
};

/* ───────── adapter over the parallel session's ServiceCatalogEntry[] ────── */
/*
 * The drop-in helper for the parallel catalog. It is intentionally LOOSELY
 * typed (a structural subset) so this file does NOT import rateCard.ts — the
 * lane fence forbids touching the money/quote files, and a structural shape
 * keeps the seam additive. When the parallel session adds `intakeStages` +
 * keeps `name`, swap the export below to:
 *     export const activeIntakeCatalog = catalogFromServiceEntries(REFRESHED_CATALOGUE);
 */

/** The structural subset of ServiceCatalogEntry this adapter reads. */
export interface ServiceCatalogEntryLike {
  /** maps to a spine tier key (svcId for L1A..L3B; ancillary svcIds map below). */
  svcId: string;
  /** the live client-facing name (already canonical Set-4 in the parallel catalog). */
  name: string;
  /** the OPTIONAL additive field the parallel session adds. */
  intakeStages?: IntakeStageSpec[];
}

/**
 * Map the parallel catalog's svcId → our spine tier key. L1A..L3B match 1:1;
 * the ancillary svcIds differ (ADDON_SITE_VISIT etc.), so they are bridged.
 */
const SVC_ID_TO_TIER: Record<string, string> = {
  ADDON_SITE_VISIT: 'ANCILLARY_SITE_VISIT',
  ADDON_MAP_MAKING: 'ANCILLARY_SURVEY_MAP',
  // L1A..L3B, ASTROLOGY, WELLNESS map by identity.
};

/**
 * Build an `IntakeServiceCatalog` over the parallel session's entries. NAME is
 * taken from their entry (the live authority). STAGES are taken from their
 * `intakeStages` when present, else FALL BACK to the spine-derived stages — so
 * the swap is safe before they add the field, and incrementally takes effect as
 * they populate it per service.
 */
export function catalogFromServiceEntries(
  entries: ServiceCatalogEntryLike[],
): IntakeServiceCatalog {
  const byTier = new Map<string, ServiceCatalogEntryLike>();
  for (const e of entries) {
    const tier = SVC_ID_TO_TIER[e.svcId] || e.svcId.toUpperCase();
    byTier.set(tier, e);
  }
  return {
    name(tier: string): string {
      const e = byTier.get(spineTierKey(tier));
      if (e?.name) return e.name;
      return spineIntakeCatalog.name(tier);
    },
    intakeStages(tier: string): IntakeStageSpec[] {
      const e = byTier.get(spineTierKey(tier));
      if (e?.intakeStages && e.intakeStages.length) return e.intakeStages;
      return spineIntakeCatalog.intakeStages(tier);
    },
    tiers(): string[] {
      const fromEntries = Array.from(byTier.keys());
      const fromSpine = spineIntakeCatalog.tiers();
      return Array.from(new Set([...fromEntries, ...fromSpine]));
    },
  };
}

/**
 * THE ACTIVE CATALOG the intake reads. Today it is the spine-sourced local
 * implementation. THE ONE-LINE SWAP for the parallel session:
 *     export const activeIntakeCatalog = catalogFromServiceEntries(REFRESHED_CATALOGUE);
 */
export const activeIntakeCatalog: IntakeServiceCatalog = spineIntakeCatalog;
